import React, { useEffect, memo, useCallback, useRef, useState } from 'react';
import './App.css';
import Login from './pages/Login';
import Sidebar from './pages/components/Sidebar';
import MessagesList from './pages/components/MessagesList';
import InputArea from './pages/components/InputArea';
import SettingsPanel from './pages/components/SettingsPanel';
// import FileActivityPanel from './components/FileActivityPanel';  // 2026-09-20 用户裁决：任务动态显示不正确，先注释隐藏
import { useAuth } from './hooks/useAuth';
import { useChat } from './hooks/useChat';
import { chatService } from './services/chatService';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { usePerformanceMonitor } from './hooks/usePerformance';

const App: React.FC = memo(() => {
    const { measureFunction } = usePerformanceMonitor('App');
    const { user, isAuthenticated, logout, accessToken } = useAuth();
    // 闲置超时自动登出（30 分钟无操作 → 跳转登录页）
    useIdleTimeout();
    // 设置面板
    const [showSettings, setShowSettings] = useState(false);
    const handleOpenSettings = useCallback(() => setShowSettings(true), []);
    const handleCloseSettings = useCallback(() => setShowSettings(false), []);
    const {
        messages,
        isTyping,
        files,
        currentSessionId,
        chatSessions,
        isLoadingSessions,
        sendMessage,
        startManualEntry,
        handleFileSelect,
        removeFile,
        clearFiles,
        fetchChatSessions,
        switchToSession,
        deleteChatSession,
        renameChatSession,
        handleNewChat,
        updateMessage,
    } = useChat();

    // 全局拖拽上传：拖入窗口任意位置显示遮罩，松手交给 handleFileSelect（对齐离线版）
    const [dragActive, setDragActive] = useState(false);
    const dragCounterRef = useRef(0);

    // 输入区高度拖拽（2026-09-18 用户令）
    const inputWrapRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const saved = parseInt(localStorage.getItem('input_area_height') || '');
        if (saved >= 60 && saved <= 600 && inputWrapRef.current) {
            inputWrapRef.current.style.height = saved + 'px';
        }
    }, []);
    const startVDrag = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const startY = e.clientY;
        const el = inputWrapRef.current;
        if (!el) return;
        const startH = el.getBoundingClientRect().height;
        const onMove = (ev: MouseEvent) => {
            const h = Math.min(600, Math.max(60, startH + (startY - ev.clientY)));
            el.style.height = h + 'px';
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            document.body.classList.remove('v-dragging');
            const h = parseInt(el.style.height);
            if (h) localStorage.setItem('input_area_height', String(h));
        };
        document.body.classList.add('v-dragging');
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, []);

    useEffect(() => {
        const onDragEnter = (e: DragEvent) => {
            e.preventDefault();
            dragCounterRef.current += 1;
            if (e.dataTransfer?.types?.includes('Files')) setDragActive(true);
        };
        const onDragOver = (e: DragEvent) => {
            e.preventDefault();
        };
        const onDragLeave = (e: DragEvent) => {
            e.preventDefault();
            dragCounterRef.current -= 1;
            if (dragCounterRef.current <= 0) {
                dragCounterRef.current = 0;
                setDragActive(false);
            }
        };
        const onDrop = (e: DragEvent) => {
            e.preventDefault();
            dragCounterRef.current = 0;
            setDragActive(false);
            const dropped = Array.from(e.dataTransfer?.files || []);
            if (dropped.length) handleFileSelect(dropped);
        };
        document.addEventListener('dragenter', onDragEnter);
        document.addEventListener('dragover', onDragOver);
        document.addEventListener('dragleave', onDragLeave);
        document.addEventListener('drop', onDrop);
        return () => {
            document.removeEventListener('dragenter', onDragEnter);
            document.removeEventListener('dragover', onDragOver);
            document.removeEventListener('dragleave', onDragLeave);
            document.removeEventListener('drop', onDrop);
        };
    }, [handleFileSelect]);

    // 获取聊天历史
    useEffect(() => {
        console.log('🔄 App useEffect触发:', { 
            isAuthenticated, 
            hasUser: !!user, 
            hasAccessToken: !!accessToken,
            userAuthString: user?.authString 
        });
        
        if (isAuthenticated && (user?.authString || accessToken)) {
            console.log('✅ 开始获取聊天历史...');
            fetchChatSessions();
        } else {
            console.log('❌ 跳过获取聊天历史，认证信息不完整');
        }
    }, [isAuthenticated, user?.authString, accessToken, fetchChatSessions]);

    // 变异确认成功（由内联确认卡片 dispatch）后刷新会话列表
    useEffect(() => {
        const handleConfirmationSuccess = () => {
            if (user?.authString || accessToken) {
                fetchChatSessions();
            }
        };

        window.addEventListener('variant-confirmation-success', handleConfirmationSuccess);
        return () => {
            window.removeEventListener('variant-confirmation-success', handleConfirmationSuccess);
        };
    }, [user?.authString, accessToken, fetchChatSessions]);

    // 2026-09-18（用户实测抓出）：会话内产生首条消息后自动刷新侧栏历史——
    // 否则新会话分析跑完了，侧栏还停在"暂无历史记录"，只能重新登录才看见
    // 注意时序：消息往往先于 sessionId 到达，ref 记"已拉过"会漏——
    // 改布尔边沿触发：消息有无翻转 或 sessionId 到位，任一变化且两者皆具即拉
    const hasMessages = messages.length > 0;
    useEffect(() => {
        if (currentSessionId && hasMessages) {
            fetchChatSessions();
        }
    }, [currentSessionId, hasMessages, fetchChatSessions]);

    // 分析收尾（isTyping 翻落）再拉一次：后端此时已写语义标题（0918 用户令：
    // "第一个回答好了就要自动刷新，会话名规则起效"）
    const wasTypingRef = useRef(false);
    useEffect(() => {
        if (wasTypingRef.current && !isTyping && currentSessionId) {
            fetchChatSessions();
        }
        wasTypingRef.current = isTyping;
    }, [isTyping, currentSessionId, fetchChatSessions]);

    const handleLoginSuccess = useCallback((userData: any) => {
        console.log('Login successful:', userData.username);
    }, []);

    // 2026-09-18 用户令：默认提问预填只为"第一轮分析"服务——
    // 仅全新会话（点新建对话/首屏）且消息为空时才预填；
    // finalOut 出来后/任意一轮问答后/切进历史会话，一律不再自动填充
    const [freshSession, setFreshSession] = useState(true);
    const handleNewChatWrapped = useCallback(() => {
        setFreshSession(true);
        handleNewChat();
    }, [handleNewChat]);
    const switchToSessionWrapped = useCallback((sessionId: string, sessionName: string) => {
        setFreshSession(false);
        switchToSession(sessionId, sessionName);
    }, [switchToSession]);

    const handleSend = useCallback(async (inputText: string) => {
        console.log('🎯 App handleSend called with:', inputText);
        const measuredSendMessage = measureFunction(sendMessage, 'sendMessage');
        try {
            console.log('📞 Calling sendMessage...');
            await measuredSendMessage(inputText);
            console.log('✅ sendMessage completed');
        } catch (error) {
            console.error('❌ 发送消息失败:', error);
        }
    }, [sendMessage, measureFunction]);

    const handleClearChat = useCallback(() => {
        if (window.confirm('确定要清空所有聊天记录吗？')) {
            handleNewChatWrapped();
            if (user?.authString) {
                fetchChatSessions();
            }
        }
    }, [handleNewChatWrapped, user?.authString, fetchChatSessions]);

    // 消息点赞功能（2026-09-18 顺带持久化：有服务端 ID 即落库，失败不阻塞）
    const handleLike = useCallback((id: number, liked: boolean | null) => {
        const msg = messages.find(m => m.id === id);
        if (msg?.serverId && user?.authString) {
            chatService.submitFeedback(msg.serverId, { liked }, user.authString)
                .catch(e => console.warn('赞踩持久化失败（不阻塞）:', e));
        }
    }, [messages, user?.authString]);

    // 2026-09-18 用户令：意见反馈按钮——有 serverId 直挂消息；
    // 无（确定性结论卡本地补建）按 session+原文匹配/补落后台（沿用 Kevin feedback 设施）
    const handleFeedback = useCallback(async (id: number, text: string) => {
        const msg = messages.find(m => m.id === id);
        if (!msg || !user?.authString) return;
        try {
            let serverId = msg.serverId;
            if (serverId) {
                await chatService.submitFeedback(serverId, { feedback_text: text }, user.authString);
            } else {
                const resp = await chatService.submitFeedbackByContent(
                    { session_id: currentSessionId!, match_text: msg.text, feedback_text: text },
                    user.authString);
                serverId = (resp as any)?.message_id || serverId;
            }
            updateMessage(id, { feedbackText: text, ...(serverId ? { serverId } : {}) } as any);
        } catch (e) {
            console.error('反馈提交失败:', e);
            updateMessage(id, { notice: '反馈提交失败，请稍后重试' } as any);
        }
    }, [messages, user?.authString, currentSessionId, updateMessage]);

    // 消息复制功能
    const handleCopy = useCallback((text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            console.log('消息已复制到剪贴板');
            // 可以添加一个提示，比如 toast 消息
        }).catch(err => {
            console.error('复制失败:', err);
        });
    }, []);

    // 如果未认证，显示登录页面
    if (!isAuthenticated) {
        return <Login onLoginSuccess={handleLoginSuccess} />;
    }

    return (
        <div className="app-container">
            {/* 拖拽上传遮罩（对齐离线版 drag-overlay 样式） */}
            <div className={`drag-overlay ${dragActive ? 'show' : ''}`}>
                <div className="drag-overlay-content">
                    <div className="drag-overlay-icon">📂</div>
                    <div className="drag-overlay-text">松开鼠标上传文件</div>
                </div>
            </div>

            <Sidebar
                user={user}
                currentSessionId={currentSessionId}
                chatSessions={chatSessions}
                isLoadingSessions={isLoadingSessions}
                onSwitchToSession={switchToSessionWrapped}
                onDeleteSession={deleteChatSession}
                onRenameSession={renameChatSession}
                onClearChat={handleClearChat}
                onNewChat={handleNewChatWrapped}
                onRefreshSessions={fetchChatSessions}
                onLogout={logout}
                onOpenSettings={handleOpenSettings}
            />

            <div className="main-chat">
                {/* <FileActivityPanel />  2026-09-20 用户裁决：任务动态显示不正确，先注释隐藏 */}

                <MessagesList
                    messages={messages}
                    isTyping={isTyping}
                    onLike={handleLike}
                    onFeedback={handleFeedback}
                    onCopy={handleCopy}
                />

                {/* 2026-09-18 用户令：消息区|输入区 分隔线可拖（上下分配高度，localStorage 记忆） */}
                <div className="v-resizer" onMouseDown={startVDrag} title="拖动调整输入区高度" />
                <div className="input-area-wrap" ref={inputWrapRef}>
                    <InputArea
                        files={files}
                        onSend={handleSend}
                        onFileSelect={handleFileSelect}
                        onRemoveFile={removeFile}
                        onClearFiles={clearFiles}
                        onManualEntry={startManualEntry}
                        prefillSignal={currentSessionId}
                        shouldPrefill={freshSession && messages.length === 0}
                    />
                </div>

                <footer className="app-icp-footer">
                    <a
                        href="https://beian.miit.gov.cn/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        沪ICP备2026042261号
                    </a>
                </footer>
            </div>

            {/* 设置面板：运行状态 + 模型与功能配置 */}
            <SettingsPanel visible={showSettings} onClose={handleCloseSettings} />
        </div>
    );
});

App.displayName = 'App';

export default App;